import React from 'react'
import styled from 'styled-components'
import { CardElement, ReactStripeElements } from 'react-stripe-elements'
import {
  colorConstants,
  colors,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import Form from '@jarvis.oddle/core/components/Form'
import { P } from '@jarvis.oddle/core/components/Typo'

interface ICardElement extends ReactStripeElements.ElementProps {
  name: string
  isFocus: boolean
  isError: boolean
}

const getBorderColor = (props) => {
  if (props.isError) {
    return colorConstants.red
  } else if (props.isFocus) {
    return colors.text
  } else if (props.isComplete) {
    return colorConstants.green
  } else {
    return colors.separator
  }
}

/**
 * This component will render credit card input from stripe
 * which allow user to insert their cc number, expiry date,
 * and cvc
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const StyledCardElement = styled(CardElement as ICardElement).attrs(
  () => ({
    hidePostalCode: true
  })
)<{
  isComplete: boolean
}>`
  border: 1px ${(props) => getBorderColor(props)} solid;
  padding: ${padding[paddingSizes.medium]} ${padding[paddingSizes.small]};
  background-color: ${(props) =>
    props.isComplete ? colorConstants.lightGreen : colorConstants.white};
  border-radius: 2px;
`

const StyledMessageError = styled(P).attrs(() => ({
  marginTop: marginSizes.extraSmall,
  mobileFontSize: '14px',
  colors: colorConstants.white
}))`
  padding: 5px 10px;
  background-color: ${colorConstants.red};
  display: table-cell;
  color: white;
  border-radius: 0 0 2px 2px;
`

const StripeCardElement = ({
  isError,
  onCardChange,
  onStripeReady,
  errorCard,
  validCard,
  title
}: {
  isError: boolean
  onCardChange
  onStripeReady
  errorCard?: string
  validCard: boolean
  title: string
}): JSX.Element => {
  const { t } = React.useContext(TranslationContext)
  const [isFocus, setFocus] = React.useState(false)

  /**
   * isFocus and isError that are passed to the CardElement component
   * are meant to display different container's border color
   *
   * The existence of these two props will not influence
   * the outcome of the payment request
   */
  const onFocus = () => {
    setFocus(true)
  }

  const onBlur = () => {
    setFocus(false)
  }

  return (
    <React.Fragment>
      <P bold>{title || t('Enter Your Payment Details')}</P>
      <Form.Input name='cardName' label={t('Name on Card')} />
      <StyledCardElement
        name='card'
        isFocus={isFocus}
        isError={isError}
        onChange={onCardChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onReady={onStripeReady}
        isComplete={validCard}
      />
      {isError && <StyledMessageError>{errorCard}</StyledMessageError>}
    </React.Fragment>
  )
}

export default StripeCardElement
